import { Chip, Grid, Stack, Table, TableBody, TableHead, TableRow } from '@mui/material';

import {
  GeneralTableBox,
  HeadTableCell,
  PaginationTable,
  RowTableCell,
} from '../../../../common/components';
import {
  formatDateTime,
  formatInstallments,
  formatPuntajePoints,
  getMovementChipColor,
  getMovementSourceLabel,
  getMovementTypeLabel,
} from '../../utils/puntajeFormatters';
import MetricCard from '../common/MetricCard';
import SuggestionCard from '../common/SuggestionCard';
import TableStateRow from '../common/TableStateRow';

const FundSection = ({
  title,
  summary,
  movements,
  isLoading,
  isAdmin,
  onConfirmSuggestion,
  confirmLoading,
  pagination,
  onCouponClick,
  onContractClick,
  showScoreColumn = false,
}) => (
  <Stack>
    <Grid container bgcolor="white">
      <Grid item xs={12} sm={6} md={3} p={1.5}>
        <MetricCard
          title="Saldo apertura en cuotas"
          value={formatInstallments(summary?.opening_balance_installments)}
          subtitle="Cuotas equivalentes al inicio del periodo"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3} p={1.5}>
        <MetricCard
          title="Creditos en cuotas"
          value={formatInstallments(summary?.credited_installments)}
          subtitle="Ingresos del periodo"
          color="success.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3} p={1.5}>
        <MetricCard
          title="Debitos en cuotas"
          value={formatInstallments(summary?.debited_installments)}
          subtitle="Impacto de preadjudicaciones"
          color="error.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3} p={1.5}>
        <MetricCard
          title="Saldo final en cuotas"
          value={formatInstallments(summary?.final_balance_installments)}
          subtitle={`Objetivo: ${formatInstallments(summary?.target_installments)}`}
          color="primary.dark"
        />
      </Grid>
      {showScoreColumn ? (
        <>
          <Grid item xs={12} sm={6} md={3} p={1.5}>
            <MetricCard
              title="Saldo apertura en puntos"
              value={formatPuntajePoints(summary?.opening_balance_installments)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} p={1.5}>
            <MetricCard
              title="Creditos en puntos"
              value={formatPuntajePoints(summary?.credited_installments)}
              color="success.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} p={1.5}>
            <MetricCard
              title="Debitos en puntos"
              value={formatPuntajePoints(summary?.debited_installments)}
              color="error.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} p={1.5}>
            <MetricCard
              title="Saldo final en puntos"
              value={formatPuntajePoints(summary?.final_balance_installments)}
              color="primary.dark"
            />
          </Grid>
        </>
      ) : null}
    </Grid>

    <SuggestionCard
      suggestion={summary?.active_suggestion}
      title={`Sugerencia activa | ${title}`}
      isAdmin={isAdmin}
      onConfirm={onConfirmSuggestion}
      isLoading={confirmLoading}
    />

    <GeneralTableBox>
      <Table>
        <TableHead>
          <TableRow>
            <HeadTableCell>Tipo</HeadTableCell>
            <HeadTableCell>Fecha</HeadTableCell>
            <HeadTableCell>Descripcion</HeadTableCell>
            <HeadTableCell>Contrato</HeadTableCell>
            <HeadTableCell>Cupón</HeadTableCell>
            <HeadTableCell>Origen</HeadTableCell>
            <HeadTableCell sx={{ textAlign: 'right' }}>Cuotas</HeadTableCell>
            {showScoreColumn ? (
              <HeadTableCell sx={{ textAlign: 'right' }}>Puntaje</HeadTableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {!movements.length ? (
            <TableStateRow
              colSpan={showScoreColumn ? 8 : 7}
              loading={isLoading}
              emptyMessage="No hay movimientos para este fondo en el periodo seleccionado."
            />
          ) : (
            movements.map((movement, index) => (
              <TableRow
                key={`${movement.uuid_coupon || movement.uuid_pre_adjudication || index}`}
                hover
              >
                <RowTableCell>
                  <Chip
                    label={getMovementTypeLabel(movement.movement_type)}
                    color={getMovementChipColor(movement.movement_type)}
                    size="small"
                    variant="outlined"
                  />
                </RowTableCell>
                <RowTableCell>{formatDateTime(movement.movement_date)}</RowTableCell>
                <RowTableCell>{movement.description || '-'}</RowTableCell>
                <RowTableCell>
                  {movement.uuid_contract ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => onContractClick?.(movement)}
                      onKeyDown={(e) => e.key === 'Enter' && onContractClick?.(movement)}
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      {movement.contract_number || '-'}
                    </span>
                  ) : (
                    movement.contract_number || '-'
                  )}
                </RowTableCell>
                <RowTableCell>
                  {movement.uuid_coupon ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => onCouponClick?.(movement)}
                      onKeyDown={(e) => e.key === 'Enter' && onCouponClick?.(movement)}
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      {movement.coupon_code_number || '-'}
                    </span>
                  ) : (
                    movement.coupon_code_number || '-'
                  )}
                </RowTableCell>
                <RowTableCell>{getMovementSourceLabel(movement.source_type)}</RowTableCell>
                <RowTableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                  {formatInstallments(movement.installments_amount)}
                </RowTableCell>
                {showScoreColumn ? (
                  <RowTableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                    {formatPuntajePoints(movement.installments_amount)}
                  </RowTableCell>
                ) : null}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
    {!!pagination?.totalCount && (
      <PaginationTable
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        rowsPerPage={pagination.rowsPerPage}
        onPageChange={pagination.handlePageChange}
        onRowsPerPageChange={pagination.handleRowsPerPageChange}
      />
    )}
  </Stack>
);

export default FundSection;
